import React from 'react';
import { Alert, Badge, Button, Modal } from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiLock,
    FiShield,
    FiTarget,
    FiUsers,
    FiXCircle,
} from 'react-icons/fi';
import {
    ADMIN_ROLES_WITH_DETAILS,
    IBAN_VISIBILITY_CONFIG,
    SENSITIVE_DATA_GENERAL_RULES,
    getAdminRoleDetails,
} from '../../constants/adminRoles';
import './RoleDetailsModal.css';

const TITLE_ID = 'role-details-modal-title';

const RoleSection = ({ icon: Icon, title, children }) => (
    <section className="role-details__section">
        <h3 className="role-details__section-title">
            <Icon aria-hidden="true" className="me-2" />
            {title}
        </h3>
        {children}
    </section>
);

const RoleList = ({ items, icon: Icon, className }) => (
    <ul className={`role-details__list ${className || ''}`}>
        {items.map((item) => (
            <li key={item}>
                <Icon aria-hidden="true" className="role-details__list-icon" />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const RoleSwitcher = ({ selected, onSelect }) => (
    <div className="role-details__switcher" role="tablist" aria-label="Choisir un rôle">
        {ADMIN_ROLES_WITH_DETAILS.map((item) => {
            const isActive = item.value === selected;
            return (
                <button
                    key={item.value}
                    type="button"
                    role="tab"
                    id={`role-details-tab-${item.value}`}
                    aria-selected={isActive}
                    aria-controls="role-details-panel"
                    className={`role-details__switcher-btn${isActive ? ' is-active' : ''}`}
                    onClick={() => !isActive && onSelect(item.value)}
                >
                    {item.label}
                </button>
            );
        })}
    </div>
);

/**
 * Fiche descriptive d'un rôle back-office.
 * `role` : valeur API (ex. "Restricted_admin") ; `onRoleChange` active le sélecteur de rôle.
 */
const RoleDetailsModal = ({ show, role, onHide, onRoleChange }) => {
    const details = getAdminRoleDetails(role);
    const ibanConfig = details ? IBAN_VISIBILITY_CONFIG[details.sensitiveData.visibility] : null;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            scrollable
            fullscreen="sm-down"
            aria-labelledby={TITLE_ID}
            className="role-details-modal"
        >
            <Modal.Header closeButton closeLabel="Fermer la fenêtre">
                <Modal.Title id={TITLE_ID} as="h2" className="role-details__title">
                    <FiShield aria-hidden="true" className="me-2" />
                    {details ? details.label : 'Détails du rôle'}
                    {details && <code className="role-details__code">{details.value}</code>}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {onRoleChange && (
                    <RoleSwitcher selected={details?.value} onSelect={onRoleChange} />
                )}

                {!details ? (
                    <Alert variant="secondary" className="mb-0">
                        Aucun détail disponible pour ce rôle{role ? ` (« ${role} »)` : ''}.
                    </Alert>
                ) : (
                    <div
                        id="role-details-panel"
                        role={onRoleChange ? 'tabpanel' : undefined}
                        aria-labelledby={onRoleChange ? `role-details-tab-${details.value}` : undefined}
                    >
                        <dl className="role-details__summary">
                            <div>
                                <dt>Niveau d'accès</dt>
                                <dd>
                                    <Badge
                                        bg={details.accessVariant}
                                        text={['warning', 'light'].includes(details.accessVariant) ? 'dark' : undefined}
                                    >
                                        {details.accessLevel}
                                    </Badge>
                                </dd>
                            </div>
                            <div>
                                <dt>Pour qui</dt>
                                <dd>
                                    <FiUsers aria-hidden="true" className="me-1" />
                                    {details.audience}
                                </dd>
                            </div>
                        </dl>

                        <RoleSection icon={FiCheckCircle} title="Autorisations">
                            <RoleList items={details.permissions} icon={FiCheckCircle} className="is-allowed" />
                        </RoleSection>

                        {details.objective && (
                            <RoleSection icon={FiTarget} title="Objectif">
                                <p className="mb-0">{details.objective}</p>
                            </RoleSection>
                        )}

                        <RoleSection icon={FiXCircle} title="Restrictions">
                            <RoleList items={details.restrictions} icon={FiXCircle} className="is-restricted" />
                        </RoleSection>

                        <RoleSection icon={FiLock} title="Données masquées et IBAN">
                            <div className="role-details__sensitive">
                                <Badge
                                    bg={ibanConfig.variant}
                                    text={ibanConfig.variant === 'warning' ? 'dark' : undefined}
                                    className="mb-2"
                                >
                                    {ibanConfig.label}
                                </Badge>
                                <RoleList items={details.sensitiveData.rules} icon={FiLock} />
                                <div className="role-details__sensitive-note">
                                    <FiAlertTriangle aria-hidden="true" className="me-2 flex-shrink-0" />
                                    <ul className="mb-0">
                                        {SENSITIVE_DATA_GENERAL_RULES.map((rule) => (
                                            <li key={rule}>{rule}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </RoleSection>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                    Fermer
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RoleDetailsModal;
